import { Literal, Static, Union } from "runtypes";

import { getNormalEnum } from "./runtypeEnums.js";
import { typedObjectValues } from "./utils/typedObjects.js";

export const LearnStepLiteral = Union(
  // Legacy steps
  Literal("LEARN_MORE"),
  Literal("ADD_SCHEMA_METADATA"),

  Literal("ADD_AI_CONTEXT"),
  Literal("ADD_CONNECTION"),
  Literal("CREATE_PROJECT"),
  Literal("PUBLISH_APP"),
  Literal("TELL_A_FRIEND"),

  // Sub-steps
  Literal("ENDORSE_SCHEMA"),
  Literal("CREATE_SEMANTIC_PROJECT"),
  Literal("ADD_WORKSPACE_RULES_FILE"),
);
export type LearnStepLiteral = Static<typeof LearnStepLiteral>;

export type StoredLearnSteps = Partial<Record<LearnStep, boolean>>;
export type ClientLearnSteps = Record<LearnStep, boolean>;

export type LearnStep = Static<typeof LearnStepLiteral>;
export const LearnStep = getNormalEnum(LearnStepLiteral);

export const EditorLearnSteps = [
  LearnStep.CREATE_PROJECT,
  LearnStep.PUBLISH_APP,
  LearnStep.TELL_A_FRIEND,
] as const;

export const AdminLearnSteps = [
  LearnStep.ADD_CONNECTION,
  LearnStep.CREATE_PROJECT,
  LearnStep.ADD_AI_CONTEXT,
  LearnStep.PUBLISH_APP,
  LearnStep.TELL_A_FRIEND,
] as const;

export const LearnStepsWithSubsteps: Partial<Record<LearnStep, LearnStep[]>> = {
  [LearnStep.ADD_AI_CONTEXT]: [
    LearnStep.ENDORSE_SCHEMA,
    LearnStep.CREATE_SEMANTIC_PROJECT,
    LearnStep.ADD_WORKSPACE_RULES_FILE,
  ],
};

export const countFractionalCompletedSteps = (
  step: LearnStep,
  learnSteps: ClientLearnSteps,
  substepsToRemove: Set<LearnStep>,
): number | undefined => {
  if (
    !(step in LearnStepsWithSubsteps) ||
    !LearnStepsWithSubsteps[step] ||
    LearnStepsWithSubsteps[step].length === 0
  ) {
    return undefined;
  }
  const substeps = LearnStepsWithSubsteps[step].filter(
    (substep) => !substepsToRemove.has(substep),
  );
  const completedSubsteps = substeps.filter((substep) => learnSteps[substep]);
  return completedSubsteps.length / substeps.length;
};

export const getLearnStepsWithDefaults = (
  storedLearnSteps: StoredLearnSteps | null | undefined,
): ClientLearnSteps => {
  return typedObjectValues(LearnStep).reduce((acc, step) => {
    acc[step] = storedLearnSteps?.[step] ?? false;
    return acc;
  }, {} as ClientLearnSteps);
};
